import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { tap, catchError } from 'rxjs/operators';
import { EMPTY, of } from 'rxjs';

import { PrinterService } from '../../services/printer.service';
import {
  Printer,
  PrintJob,
  PrintJobFilters,
  PrinterPagination
} from '../../interfaces/printer.interface';
import {
  ClearError,
  ClearPrintJobs,
  ClearSelectedPrinter,
  LoadPrinters,
  LoadPrintersFailure,
  LoadPrintersSuccess,
  LoadPrintJobs,
  LoadPrintJobsFailure,
  LoadPrintJobsSuccess,
  RefreshPrinterStatus,
  RefreshPrinterStatusFailure,
  RefreshPrinterStatusSuccess,
  ReprintBatch,
  ReprintBatchFailure,
  ReprintBatchSuccess,
  ReprintJob,
  ReprintJobFailure,
  SelectPrinter,
  SelectPrinterFailure,
  SelectPrinterSuccess,
  UpdateFilters
} from './printers.action';
import { ToastService } from 'src/app/services/toast/toast.service';
import { HttpErrorResponse } from '@angular/common/http';

export interface PrintersStateModel {
  printers: Printer[];
  selectedPrinter: Printer | null;
  printJobs: PrintJob[];
  pagination: PrinterPagination;
  filters: PrintJobFilters;
  loading: boolean;
  error: string | null;
}

@State<PrintersStateModel>({
  name: 'printers',
  defaults: {
    printers: [],
    selectedPrinter: null,
    printJobs: [],
    pagination: null,
    filters: {
      type: undefined,
      page: 1,
      per_page: 10,
      sort_by: 'created_at',
      sort_order: 'desc',
      search: ''
    },
    loading: false,
    error: null
  }
})
@Injectable()
export class PrintersState {
  constructor(
    private toastService: ToastService,
    private printerService: PrinterService
  ) {}

  @Selector()
  static getPrinters(state: PrintersStateModel) {
    return state.printers;
  }

  @Selector()
  static getSelectedPrinter(state: PrintersStateModel) {
    return state.selectedPrinter;
  }

  @Selector()
  static getPrintJobs(state: PrintersStateModel) {
    return state.printJobs;
  }

  @Selector()
  static getPagination(state: PrintersStateModel) {
    return state.pagination;
  }

  @Selector()
  static getFilters(state: PrintersStateModel) {
    return state.filters;
  }

  @Selector()
  static getLoading(state: PrintersStateModel) {
    return state.loading;
  }

  @Selector()
  static getError(state: PrintersStateModel) {
    return state.error;
  }

  @Action(LoadPrinters)
  loadPrinters(ctx: StateContext<PrintersStateModel>) {
    ctx.patchState({ loading: true, error: null });

    return this.printerService.loadPrinters().pipe(
      tap((res) => {
        ctx.dispatch(new LoadPrintersSuccess(res));
      }),
      catchError((error) => {
        ctx.dispatch(new LoadPrintersFailure(error.message));
        return of(error);
      })
    );
  }

  @Action(LoadPrintersSuccess)
  loadPrintersSuccess(
    ctx: StateContext<PrintersStateModel>,
    { printers }: LoadPrintersSuccess
  ) {
    ctx.patchState({ printers, loading: false });
  }

  @Action(LoadPrintersFailure)
  loadPrintersFailure(
    ctx: StateContext<PrintersStateModel>,
    { error }: LoadPrintersFailure
  ) {
    ctx.patchState({ loading: false, error });
  }

  @Action(SelectPrinter)
  selectPrinter(
    ctx: StateContext<PrintersStateModel>,
    { printerId }: SelectPrinter
  ) {
    ctx.patchState({ loading: true, error: null });

    return this.printerService.loadPrinterById(printerId).pipe(
      tap((printer) => {
        ctx.dispatch(new SelectPrinterSuccess(printer));
      }),
      catchError((error) => {
        ctx.dispatch(new SelectPrinterFailure(error.message));
        return of(error);
      })
    );
  }

  @Action(SelectPrinterSuccess)
  selectPrinterSuccess(
    ctx: StateContext<PrintersStateModel>,
    { printer }: SelectPrinterSuccess
  ) {
    ctx.patchState({ selectedPrinter: printer, loading: false });
  }

  @Action(SelectPrinterFailure)
  selectPrinterFailure(
    ctx: StateContext<PrintersStateModel>,
    { error }: SelectPrinterFailure
  ) {
    ctx.patchState({ loading: false, error });
  }

  @Action(LoadPrintJobs, { cancelUncompleted: true })
  loadPrintJobs(
    ctx: StateContext<PrintersStateModel>,
    { printerId, filters }: LoadPrintJobs
  ) {
    ctx.patchState({ loading: true, error: null });

    return this.printerService
      .loadPrintJobs(printerId, {
        ...filters,
        type: filters.type === 'all' ? undefined : filters.type
      })
      .pipe(
        tap((data) => {
          ctx.dispatch(new LoadPrintJobsSuccess(data));
        }),
        catchError((error) => {
          ctx.dispatch(new LoadPrintJobsFailure(error.message));
          return of(error);
        })
      );
  }

  @Action(LoadPrintJobsSuccess)
  loadPrintJobsSuccess(
    ctx: StateContext<PrintersStateModel>,
    { data }: LoadPrintJobsSuccess
  ) {
    ctx.patchState({
      printJobs: data.print_jobs,
      pagination: data.pagination,
      loading: false
    });
  }

  @Action(LoadPrintJobsFailure)
  loadPrintJobsFailure(
    ctx: StateContext<PrintersStateModel>,
    { error }: LoadPrintJobsFailure
  ) {
    ctx.patchState({ loading: false, error });
  }

  @Action(ReprintJob)
  reprintJob(
    ctx: StateContext<PrintersStateModel>,
    { printerId, jobId }: ReprintJob
  ) {
    return this.printerService.reprintJob(printerId, jobId).pipe(
      tap(({ message }) => {
        this.toastService.success(message);
      }),
      catchError((error: HttpErrorResponse) => {
        const errorMessage = error.error?.message || error.message;
        ctx.dispatch(new ReprintJobFailure(errorMessage));
        return EMPTY;
      })
    );
  }

  // @Action(ReprintJobSuccess)
  // reprintJobSuccess(ctx: StateContext<PrintersStateModel>) {
  //   ctx.patchState({ loading: false });
  // }

  @Action(ReprintJobFailure)
  reprintJobFailure(
    ctx: StateContext<PrintersStateModel>,
    { error }: ReprintJobFailure
  ) {
    ctx.patchState({ error });
  }

  @Action(RefreshPrinterStatus)
  refreshPrinterStatus(ctx: StateContext<PrintersStateModel>) {
    ctx.patchState({ loading: true, error: null });

    return this.printerService.refreshPrinterStatus().pipe(
      tap((printers) => {
        ctx.dispatch(new RefreshPrinterStatusSuccess(printers));
      }),
      catchError((error) => {
        ctx.dispatch(new RefreshPrinterStatusFailure(error.message));
        return of(error);
      })
    );
  }

  @Action(RefreshPrinterStatusSuccess)
  refreshPrinterStatusSuccess(
    ctx: StateContext<PrintersStateModel>,
    { printers }: RefreshPrinterStatusSuccess
  ) {
    ctx.patchState({ printers, loading: false });
  }

  @Action(RefreshPrinterStatusFailure)
  refreshPrinterStatusFailure(
    ctx: StateContext<PrintersStateModel>,
    { error }: RefreshPrinterStatusFailure
  ) {
    ctx.patchState({ loading: false, error });
  }

  @Action(UpdateFilters)
  updateFilters(
    ctx: StateContext<PrintersStateModel>,
    { filters }: UpdateFilters
  ) {
    ctx.patchState({ filters });
  }

  @Action(ClearSelectedPrinter)
  clearSelectedPrinter(ctx: StateContext<PrintersStateModel>) {
    ctx.patchState({ selectedPrinter: null });
  }

  @Action(ClearPrintJobs)
  clearPrintJobs(ctx: StateContext<PrintersStateModel>) {
    ctx.patchState({ printJobs: [], pagination: null });
  }

  @Action(ReprintBatch)
  reprintBatch(
    ctx: StateContext<PrintersStateModel>,
    { printerId, batchId }: ReprintBatch
  ) {
    ctx.patchState({ loading: true, error: null });

    return this.printerService.reprintBatch(printerId, batchId).pipe(
      tap(() => {
        ctx.dispatch(new ReprintBatchSuccess());
      }),
      catchError((error) => {
        ctx.dispatch(new ReprintBatchFailure(error.message));
        return of(error);
      })
    );
  }

  @Action(ReprintBatchSuccess)
  reprintBatchSuccess(ctx: StateContext<PrintersStateModel>) {
    ctx.patchState({ loading: false });
  }

  @Action(ReprintBatchFailure)
  reprintBatchFailure(
    ctx: StateContext<PrintersStateModel>,
    { error }: ReprintBatchFailure
  ) {
    ctx.patchState({ loading: false, error });
  }

  @Action(ClearError)
  clearError(ctx: StateContext<PrintersStateModel>) {
    ctx.patchState({ error: null });
  }
}
